import React, { useState, useEffect } from 'react';
import { Shield, Lock, Eye, FileText, Users, Database, Mail, ChevronRight } from 'lucide-react';
import { getText, getCurrentLanguage } from '../services/dataSync';

const PrivacyPolicyPage = () => {
  const currentYear = new Date().getFullYear();
  const lastUpdated = '2024-01-15';
  const [expandedSections, setExpandedSections] = useState({});
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [sections, setSections] = useState([]);

  // Update sections when language changes
  const updateSections = () => {
    setSections([
      { id: 'introduction', title: getText('privacy.section.introduction') || 'Introduction' },
      { id: 'collection', title: getText('privacy.section.collection') || 'Information We Collect' },
      { id: 'usage', title: getText('privacy.section.usage') || 'How We Use Your Information' },
      { id: 'sharing', title: getText('privacy.section.sharing') || 'Information Sharing and Disclosure' },
      { id: 'security', title: getText('privacy.section.security') || 'Data Security' },
      { id: 'cookies', title: getText('privacy.section.cookies') || 'Cookies and Tracking Technologies' },
      { id: 'rights', title: getText('privacy.section.rights') || 'Your Rights' },
      { id: 'retention', title: getText('privacy.section.retention') || 'Data Retention' },
      { id: 'third-party', title: getText('privacy.section.thirdParty') || 'Third-Party Links' },
      { id: 'children', title: getText('privacy.section.children') || "Children's Privacy" },
      { id: 'changes', title: getText('privacy.section.changes') || 'Changes to This Privacy Policy' },
      { id: 'contact', title: getText('privacy.section.contact') || 'Contact Us' }
    ]);
  };

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
      updateSections();
    };

    // Initial load
    updateSections();

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const sectionsList = sections.length > 0 ? sections : [
    { id: 'introduction', title: getText('privacy.section.introduction') || 'Introduction' },
    { id: 'collection', title: getText('privacy.section.collection') || 'Information We Collect' },
    { id: 'usage', title: getText('privacy.section.usage') || 'How We Use Your Information' },
    { id: 'sharing', title: getText('privacy.section.sharing') || 'Information Sharing and Disclosure' },
    { id: 'security', title: getText('privacy.section.security') || 'Data Security' },
    { id: 'cookies', title: getText('privacy.section.cookies') || 'Cookies and Tracking Technologies' },
    { id: 'rights', title: getText('privacy.section.rights') || 'Your Rights' },
    { id: 'retention', title: getText('privacy.section.retention') || 'Data Retention' },
    { id: 'third-party', title: getText('privacy.section.thirdParty') || 'Third-Party Links' },
    { id: 'children', title: getText('privacy.section.children') || "Children's Privacy" },
    { id: 'changes', title: getText('privacy.section.changes') || 'Changes to This Privacy Policy' },
    { id: 'contact', title: getText('privacy.section.contact') || 'Contact Us' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollBy(0, -80); // Offset for sticky header
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {getText('privacy.title') || 'Privacy Policy'}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
              {getText('privacy.subtitle') || 'Your privacy is important to us. This policy explains how we collect, use, and protect your information.'}
            </p>
            <div className="mt-6 flex items-center justify-center gap-4 text-sm text-blue-200">
              <span>{getText('privacy.lastUpdated') || 'Last Updated'}: {lastUpdated}</span>
              <span>•</span>
              <span>{getText('privacy.effectiveDate') || 'Effective Date'}: {lastUpdated}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Table of Contents - Professional Design */}
      <section className="bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div className="container-medium">
          <div className="py-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="bg-primary-100 p-2 rounded-lg">
                <FileText className="h-5 w-5 text-primary-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">{getText('privacy.tableOfContents') || 'Table of Contents'}</h2>
            </div>
            <nav className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
              {(sections.length > 0 ? sections : [
                { id: 'introduction', title: getText('privacy.section.introduction') || 'Introduction' },
                { id: 'collection', title: getText('privacy.section.collection') || 'Information We Collect' },
                { id: 'usage', title: getText('privacy.section.usage') || 'How We Use Your Information' },
                { id: 'sharing', title: getText('privacy.section.sharing') || 'Information Sharing and Disclosure' },
                { id: 'security', title: getText('privacy.section.security') || 'Data Security' },
                { id: 'cookies', title: getText('privacy.section.cookies') || 'Cookies and Tracking Technologies' },
                { id: 'rights', title: getText('privacy.section.rights') || 'Your Rights' },
                { id: 'retention', title: getText('privacy.section.retention') || 'Data Retention' },
                { id: 'third-party', title: getText('privacy.section.thirdParty') || 'Third-Party Links' },
                { id: 'children', title: getText('privacy.section.children') || "Children's Privacy" },
                { id: 'changes', title: getText('privacy.section.changes') || 'Changes to This Privacy Policy' },
                { id: 'contact', title: getText('privacy.section.contact') || 'Contact Us' }
              ]).map((section, index) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="group text-left px-3 py-2.5 text-xs sm:text-sm bg-gray-50 hover:bg-primary-600 text-gray-700 hover:text-black rounded-lg transition-all duration-200 border border-gray-200 hover:border-primary-600 hover:shadow-md"
                >
                  <span className="text-primary-600 group-hover:text-black font-semibold mr-1">{index + 1}.</span>
                  <span className="font-medium">{section.title}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      </section>

      {/* Content Section - Clean Professional Layout */}
      <section className="section py-12 md:py-16 bg-gray-50">
        <div className="container-medium">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8 md:p-10 lg:p-12">
            
            {/* Introduction */}
            <div id="introduction" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                {getText('privacy.section.introduction') || 'Introduction'}
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                  Industrial Fedration of Maharashtra Region ("IFM", "we", "us", or "our") is committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, 
                  use our services, or interact with us.
                </p>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  By using our website and services, you consent to the collection and use of information in accordance with this policy. 
                  If you do not agree with our policies and practices, please do not use our services.
                </p>
              </div>
            </div>

            {/* Information We Collect */}
            <div id="collection" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                1. {getText('privacy.section.collection') || 'Information We Collect'}
              </h2>
              
              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 mt-8">1.1 Personal Information</h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                We may collect personal information that you provide directly to us, including but not limited to:
              </p>
              <ul className="list-none space-y-3 mb-8">
                {[
                  'Name, email address, phone number, and postal address',
                  'Company name, business registration details, and GST number',
                  'Membership application information and payment details',
                  'Event registration information',
                  'Grievance and contact form submissions',
                  'Any other information you choose to provide'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 mt-8">1.2 Automatically Collected Information</h3>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                When you visit our website, we automatically collect certain information, including:
              </p>
              <ul className="list-none space-y-3">
                {[
                  'IP address and browser type',
                  'Device information and operating system',
                  'Pages visited and time spent on pages',
                  'Referring website addresses',
                  'Cookies and similar tracking technologies'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* How We Use Information */}
            <div id="usage" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                2. {getText('privacy.section.usage') || 'How We Use Your Information'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                We use the collected information for various purposes, including:
              </p>
              <ul className="list-none space-y-3">
                {[
                  'Processing membership applications and renewals',
                  'Managing event registrations and payments',
                  'Providing customer support and responding to inquiries',
                  'Sending important updates, newsletters, and promotional materials',
                  'Improving our website and services',
                  'Complying with legal obligations and regulatory requirements',
                  'Preventing fraud and ensuring security',
                  'Conducting research and analytics'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Information Sharing */}
            <div id="sharing" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                3. {getText('privacy.section.sharing') || 'Information Sharing and Disclosure'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Service Providers', desc: 'With trusted third-party service providers who assist us in operating our website and conducting our business' },
                  { title: 'Payment Processors', desc: 'With payment gateway providers (Razorpay and other authorized processors) to process payments securely' },
                  { title: 'Legal Requirements', desc: 'When required by law, court order, or government regulation' },
                  { title: 'Business Transfers', desc: 'In connection with any merger, acquisition, or sale of assets' },
                  { title: 'With Your Consent', desc: 'When you have given explicit consent for sharing' }
                ].map((item, index) => (
                  <div key={index} className="pl-4 border-l-4 border-gray-300 hover:border-primary-500 transition-colors">
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">{item.title}</h3>
                    <p className="text-base text-gray-700">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Data Security */}
            <div id="security" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                4. {getText('privacy.section.security') || 'Data Security'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                We implement appropriate technical and organizational security measures to protect your personal information against 
                unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-none space-y-3 mb-6">
                {[
                  'SSL encryption for data transmission',
                  'Secure server infrastructure',
                  'Regular security audits and updates',
                  'Access controls and authentication mechanisms',
                  'Employee training on data protection'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-gray-50 border-l-4 border-gray-400 p-4 rounded-r-lg">
                <p className="text-base md:text-lg text-gray-700">
                  However, no method of transmission over the Internet or electronic storage is 100% secure. 
                  While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
                </p>
              </div>
            </div>

            {/* Cookies */}
            <div id="cookies" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                5. {getText('privacy.section.cookies') || 'Cookies and Tracking Technologies'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                We use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small text files 
                stored on your device that help us:
              </p>
              <ul className="list-none space-y-3 mb-6">
                {[
                  'Remember your preferences and settings',
                  'Analyze website traffic and usage patterns',
                  'Improve website functionality',
                  'Provide personalized content'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ChevronRight className="h-5 w-5 text-primary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-base md:text-lg text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain features of our website.
              </p>
            </div>

            {/* Your Rights */}
            <div id="rights" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                6. {getText('privacy.section.rights') || 'Your Rights'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                You have the following rights regarding your personal information:
              </p>
              <div className="space-y-4 mb-6">
                {[
                  { title: 'Access', desc: 'Request access to your personal information' },
                  { title: 'Correction', desc: 'Request correction of inaccurate or incomplete information' },
                  { title: 'Deletion', desc: 'Request deletion of your personal information' },
                  { title: 'Objection', desc: 'Object to processing of your personal information' },
                  { title: 'Data Portability', desc: 'Request transfer of your data to another service provider' },
                  { title: 'Withdraw Consent', desc: 'Withdraw consent for data processing at any time' }
                ].map((item, index) => (
                  <div key={index} className="pl-4 border-l-4 border-gray-300 hover:border-primary-500 transition-colors">
                    <h3 className="font-semibold text-gray-900 mb-1 text-lg">{item.title}</h3>
                    <p className="text-base text-gray-700">{item.desc}</p>
                  </div>
                ))}
              </div>
              <div className="bg-primary-50 border-l-4 border-primary-500 p-4 rounded-r-lg">
                <p className="text-base md:text-lg text-gray-700">
                  To exercise these rights, please contact us at <a href="mailto:support@mahaifm.com" className="text-primary-600 hover:underline font-semibold">support@mahaifm.com</a>.
                </p>
              </div>
            </div>

            {/* Data Retention */}
            <div id="retention" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                7. {getText('privacy.section.retention') || 'Data Retention'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, 
                unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely 
                delete or anonymize it.
              </p>
            </div>

            {/* Third-Party Links */}
            <div id="third-party" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                8. {getText('privacy.section.thirdParty') || 'Third-Party Links'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                Our website may contain links to third-party websites, services, or applications. We are not responsible for the privacy 
                practices or content of these third-party sites. We encourage you to review the privacy policies of any third-party sites 
                you visit.
              </p>
            </div>

            {/* Children's Privacy */}
            <div id="children" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                9. {getText('privacy.section.children') || 'Children\'s Privacy'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from 
                children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </div>

            {/* Changes to Policy */}
            <div id="changes" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                10. {getText('privacy.section.changes') || 'Changes to This Privacy Policy'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy 
                on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </div>

            {/* Contact Information */}
            <div id="contact" className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-primary-600">
                11. {getText('privacy.section.contact') || 'Contact Us'}
              </h2>
              <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">Industrial Fedration of Maharashtra Region</h3>
                    <p className="text-base text-gray-700">
                      Office no.9, A-wing, Jewel of Pimpri,<br />
                      Kamla Cross Road, Pimpri, Pune-411018
                    </p>
                  </div>
                  <div className="pt-4 border-t border-gray-300 space-y-2">
                    <p className="text-base text-gray-700">
                      <span className="font-semibold">Email:</span>{' '}
                      <a href="mailto:support@mahaifm.com" className="text-primary-600 hover:underline">support@mahaifm.com</a>
                    </p>
                    <p className="text-base text-gray-700">
                      <span className="font-semibold">Phone:</span>{' '}
                      <a href="tel:9067205252" className="text-primary-600 hover:underline">9067205252</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Effective Date */}
            <div className="border-t-2 border-gray-300 pt-8 mt-12">
              <p className="text-sm md:text-base text-gray-500 text-center">
                This Privacy Policy is effective as of <strong className="text-gray-700">{lastUpdated}</strong> and applies to all information collected by IFM.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
